/**
 * biometricIdentity — Source unique de vérité pour le label et l'icône
 * du capteur biométrique de l'appareil (Face ID / Touch ID / Empreinte
 * digitale / Windows Hello / Touch ID Mac / générique).
 *
 * Détection plateforme : `getMedianPlatform()` lit le bridge natif Median
 * injecté dans la WebView (fiable, pas du User-Agent).
 *
 * ⚠️ Règle absolue : Android = JAMAIS "Face ID". Même si certains Android
 * supportent Face Unlock, le prompt natif `BiometricPrompt` gère son UI
 * lui-même — on garde un label universel "Empreinte digitale".
 */

import type { LucideIcon } from 'lucide-react';
import { ScanFace, Fingerprint, KeyRound } from 'lucide-react';
import { getMedianPlatform } from '@/utils/medianNativeBridge';

export type BiometricKind =
  | 'face-id'
  | 'touch-id'
  | 'fingerprint'
  | 'windows-hello'
  | 'mac-touch-id'
  | 'generic';

export type BiometricContext =
  | 'native-ios'
  | 'native-android'
  | 'desktop-mac'
  | 'desktop-windows'
  | 'desktop-other';

export interface BiometricIdentityResult {
  kind: BiometricKind;
  label: string;
  iconName: 'ScanFace' | 'Fingerprint' | 'KeyRound';
  context: BiometricContext;
}

const ICON_MAP: Record<BiometricIdentityResult['iconName'], LucideIcon> = {
  ScanFace,
  Fingerprint,
  KeyRound,
};

/**
 * Détection iPad fine — distingue les iPads à Touch ID (bouton Power) :
 *  - iPad mini 6 (744×1133 CSS px)
 *  - iPad 9e gen / 10e gen (810×1080, 820×1180 sans Dynamic Island)
 *
 * Les autres iPads (Pro, Air récents) → Face ID.
 *
 * Retourne `'touch-id'` ou `'face-id'`.
 */
function detectIPadKind(): 'touch-id' | 'face-id' {
  const w = Math.min(window.screen.width, window.screen.height);
  const h = Math.max(window.screen.width, window.screen.height);

  // iPad mini 6 → Touch ID dans le bouton Power
  if (w === 744 && h === 1133) return 'touch-id';
  // iPad 9e gen → bouton Home avec Touch ID
  if (w === 810 && h === 1080) return 'touch-id';
  // iPad 10e gen → Touch ID dans le bouton Power
  if (w === 820 && h === 1180) return 'touch-id';

  // iPad Pro / Air → Face ID
  return 'face-id';
}

function resolveIOS(): BiometricIdentityResult {
  const ua = navigator.userAgent;
  const isIPad = /iPad/.test(ua) ||
    (/Macintosh/.test(ua) && 'ontouchend' in document); // iPadOS 13+ se déclare comme Mac

  if (isIPad) {
    const kind = detectIPadKind();
    return kind === 'face-id'
      ? { kind: 'face-id', label: 'Face ID', iconName: 'ScanFace', context: 'native-ios' }
      : { kind: 'touch-id', label: 'Touch ID', iconName: 'Fingerprint', context: 'native-ios' };
  }

  // iPhone : screen.height ≥ 812 → modèles X+ avec Face ID
  const screenH = Math.max(window.screen.height, window.screen.width);
  if (screenH >= 812) {
    return { kind: 'face-id', label: 'Face ID', iconName: 'ScanFace', context: 'native-ios' };
  }
  return { kind: 'touch-id', label: 'Touch ID', iconName: 'Fingerprint', context: 'native-ios' };
}

function resolveDesktop(): BiometricIdentityResult {
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
  if (ua.includes('macintosh') || ua.includes('mac os')) {
    return { kind: 'mac-touch-id', label: 'Touch ID', iconName: 'Fingerprint', context: 'desktop-mac' };
  }
  if (ua.includes('windows')) {
    return { kind: 'windows-hello', label: 'Windows Hello', iconName: 'KeyRound', context: 'desktop-windows' };
  }
  return { kind: 'generic', label: 'Biométrie', iconName: 'KeyRound', context: 'desktop-other' };
}

export const biometricIdentity = {
  /**
   * Résout l'identité biométrique de l'appareil courant.
   *
   * Règles :
   *  - Bridge Median 'ios'     → Face ID ou Touch ID (heuristique fine)
   *  - Bridge Median 'android' → "Empreinte digitale" (jamais Face ID)
   *  - 'web' → desktop Touch ID (Mac), Windows Hello, ou Biométrie
   */
  resolve(): BiometricIdentityResult {
    const platform = getMedianPlatform();

    if (platform === 'ios') return resolveIOS();

    if (platform === 'android') {
      return {
        kind: 'fingerprint',
        label: 'Empreinte digitale',
        iconName: 'Fingerprint',
        context: 'native-android',
      };
    }

    // platform === 'web'
    return resolveDesktop();
  },

  /**
   * Retourne le composant icône Lucide correspondant au capteur résolu.
   */
  getIcon(): LucideIcon {
    return ICON_MAP[this.resolve().iconName];
  },
};

export default biometricIdentity;
